import React, { useCallback, useMemo } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import Select, {
    type GroupBase,
    type MenuListProps,
    type MultiValue,
    type MultiValueGenericProps,
    type MultiValueProps,
    type OptionProps,
    type SingleValueProps,
    type SingleValue,
    components as rsComponents,
} from 'react-select';
import type { Theme } from 'react-select';
import { cn } from '../../utils';
import { createReneoStyles, getReneoSelectControlHeightPx } from './reneo-select-styles';
import {
    flattenOptions,
    getOptionLabel,
    getOptionValue,
    handleMultiChange,
    handleSingleChange,
    isOptionDisabled,
    resolveMultiValue,
    resolveSingleValue,
} from './reneo-select-helpers';
import type { ReneoSelectChipTone } from './reneo-select-chip-tones';

export interface ReneoSelectOption {
    label: string;
    value: string | number;
    disabled?: boolean;
    /**
     * Optional label for selected multi-value chips when different from `label`
     * (e.g. category-qualified duplicate document types in FilterBar).
     */
    selectedLabel?: string;
}

export type ReneoSelectSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ReneoSelectMenuAction {
    label: string;
    onSelect: () => void;
}

export interface ReneoSelectOwnProps<TOption extends ReneoSelectOption, TMulti extends boolean> {
    labelKey?: keyof TOption;
    valueKey?: keyof TOption;
    optionRenderer?: (option: TOption) => React.ReactNode;
    labelRenderer?: (option: TOption | TOption[]) => React.ReactNode;
    multi?: TMulti;
    emitObject?: boolean;
    maxSelections?: number;
    selectAllNone?: boolean;
    /** Additional bulk actions rendered beside Select all / Select none in a multi-select menu. */
    multiMenuActions?: readonly ReneoSelectMenuAction[];
    /**
     * Footer inside the dropdown (Cockpit n-select `#action`). Stays visible
     * even when the option list is empty — used for IBAN “Create New”.
     */
    menuFooter?: React.ReactNode;
    menuPortalTarget?: HTMLElement | null;
    /** @default 'md' */
    size?: ReneoSelectSize;
    disabled?: boolean;
    readOnly?: boolean;
    /** When true and multi, allows creating new options by typing (multi-select only). */
    creatable?: boolean;
    /** Tints multi/single chips (e.g. approval matrix per stage). */
    chipTone?: ReneoSelectChipTone;
    /** Align selected value and placeholder inside the control (default `left`). */
    valueAlign?: 'left' | 'right';
    /** Dense table cell — same height as `Input` `sm` / `h-8` (32px). */
    tableCell?: boolean;
    /** Show checkbox before each dropdown option (FilterBar multi-select popover). */
    optionCheckboxes?: boolean;
    /** Smaller inline multi tags that wrap on one row (FilterBar multi-select popover). */
    compactMultiTags?: boolean;
    /**
     * Minimum width (px) for the dropdown menu. FilterBar popovers pass the computed
     * popover width so long labels are not clipped by the default 288px floor.
     */
    menuMinWidth?: number;
    /**
     * When set with `multi`, at most this many full tags are shown; additional selections stay selected and are summarized with a compact "+N" chip (same idea as maxTagCount).
     * Use `multiHiddenCountTitle` to provide the "+N" chip's tooltip and accessible label.
     */
    multiMaxVisibleTags?: number;
    /** `title` / `aria-label` on the "+N" chip. */
    multiHiddenCountTitle?: (hiddenCount: number) => string;
}

type ReneoValue<TOption, TMulti extends boolean> = TMulti extends true ? TOption[] | string[] : TOption | string | null | undefined;

export type ReneoSelectProps<TOption extends ReneoSelectOption = ReneoSelectOption, TMulti extends boolean = false> = ReneoSelectOwnProps<TOption, TMulti> &
    Omit<
        React.ComponentProps<typeof Select<TOption, TMulti, GroupBase<TOption>>>,
        'options' | 'value' | 'onChange' | 'getOptionLabel' | 'getOptionValue' | 'isMulti'
    > & {
        options?: readonly TOption[] | readonly GroupBase<TOption>[];
        value?: TMulti extends true ? TOption[] | string[] : TOption | string | null | undefined;
        onChange?: (value: ReneoValue<TOption, TMulti>) => void;
    };

const DEFAULT_MENU_PORTAL = typeof document !== 'undefined' ? document.body : undefined;

/** Stops wheel propagation so mouse scroll works when select is inside Radix Dialog. */
function wheelStopInnerProps(innerProps: React.HTMLAttributes<HTMLDivElement>) {
    return {
        ...innerProps,
        onWheel: (e: React.WheelEvent<HTMLDivElement>) => {
            e.stopPropagation();
            innerProps?.onWheel?.(e as React.WheelEvent<HTMLDivElement>);
        },
    };
}

function createOptionComponent<TOption extends ReneoSelectOption>(
    labelKey: keyof TOption,
    optionRenderer?: (option: TOption) => React.ReactNode,
    optionCheckboxes?: boolean,
) {
    return function ReneoOption(props: OptionProps<TOption, boolean, GroupBase<TOption>>) {
        const content = optionRenderer ? optionRenderer(props.data as TOption) : <span className="truncate min-w-0 block">{props.label}</span>;
        if (!optionCheckboxes) {
            return <rsComponents.Option {...props}>{content}</rsComponents.Option>;
        }
        return (
            <rsComponents.Option {...props}>
                <span className="flex min-w-0 items-center gap-2">
                    <span
                        className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                            props.isSelected ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white',
                        )}
                        aria-hidden
                    >
                        {props.isSelected ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className="min-w-0 truncate">{content}</span>
                </span>
            </rsComponents.Option>
        );
    };
}

function createMenuListWithWheelFix<TOption extends ReneoSelectOption>() {
    return function MenuListWithWheelFix(props: MenuListProps<TOption, boolean, GroupBase<TOption>>) {
        const { innerProps, ...rest } = props;
        return <rsComponents.MenuList {...rest} innerProps={wheelStopInnerProps(innerProps)} />;
    };
}

function createMultiValueLabelComponent<TOption extends ReneoSelectOption>(getLabel: (option: TOption) => React.ReactNode) {
    return function MultiValueLabel(props: MultiValueGenericProps<TOption, boolean>) {
        return (
            <rsComponents.MultiValueLabel {...props}>
                <span className="flex min-w-0 items-center">{getLabel(props.data)}</span>
            </rsComponents.MultiValueLabel>
        );
    };
}

function getMultiValueDisplayLabel<TOption extends ReneoSelectOption>(option: TOption, labelKey: keyof TOption): string {
    if (typeof option.selectedLabel === 'string' && option.selectedLabel.length > 0) {
        return option.selectedLabel;
    }
    return getOptionLabel(option, labelKey);
}

function createMultiValueRemoveComponent<TOption extends ReneoSelectOption>() {
    return function MultiValueRemove(props: MultiValueGenericProps<TOption, boolean>) {
        const isDisabled = 'disabled' in props.data && props.data.disabled;

        if (isDisabled) {
            return null;
        }

        return <rsComponents.MultiValueRemove {...props} />;
    };
}

function createLimitedMultiValueComponent<TOption extends ReneoSelectOption>(
    maxVisibleTags: number,
    formatHiddenCount: (hiddenCount: number) => string,
    compact?: boolean,
) {
    return function LimitedMultiValue(props: MultiValueProps<TOption, boolean, GroupBase<TOption>>) {
        if (props.index < maxVisibleTags) {
            return <rsComponents.MultiValue {...props} />;
        }

        const hiddenCount = props.getValue().length - maxVisibleTags;
        if (props.index !== maxVisibleTags || hiddenCount <= 0) {
            return null;
        }

        const label = formatHiddenCount(hiddenCount);
        return (
            <span
                className={cn(
                    'flex shrink-0 items-center rounded bg-gray-100 font-medium text-gray-700',
                    compact ? 'h-4 px-1.5 text-[10px] leading-none' : 'h-6 px-2 text-xs',
                )}
                title={label}
                aria-label={label}
            >
                {label}
            </span>
        );
    };
}

function createSingleValueComponent<TOption extends ReneoSelectOption>(labelRenderer: (option: TOption) => React.ReactNode, valueAlign?: 'left' | 'right') {
    return function ReneoSingleValue(props: SingleValueProps<TOption, false, GroupBase<TOption>>) {
        return (
            <rsComponents.SingleValue {...props}>
                <span className={cn('flex min-w-0 w-full max-w-full items-center', valueAlign === 'right' && 'justify-end text-right')}>
                    {labelRenderer(props.data)}
                </span>
            </rsComponents.SingleValue>
        );
    };
}

function createMenuListWithBulkActions<TOption extends ReneoSelectOption>(
    showSelectAllNone: boolean,
    onSelectAll: () => void,
    onSelectNone: () => void,
    additionalActions: readonly ReneoSelectMenuAction[],
    selectAllLabel: string,
    selectNoneLabel: string,
    footer?: React.ReactNode,
) {
    const showBulk = showSelectAllNone || additionalActions.length > 0;
    return function MenuListWithBulkActions(props: MenuListProps<TOption, boolean, GroupBase<TOption>>) {
        const { children, innerProps, ...rest } = props;
        return (
            <rsComponents.MenuList {...rest} innerProps={wheelStopInnerProps(innerProps)}>
                {showBulk ? (
                    <div className="flex gap-1 border-b border-gray-200 px-2 py-1.5">
                        {showSelectAllNone ? (
                            <button
                                type="button"
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onSelectAll();
                                }}
                                className="text-[12px] font-medium text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                            >
                                {selectAllLabel}
                            </button>
                        ) : null}
                        {additionalActions.map(action => (
                            <button
                                key={action.label}
                                type="button"
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    action.onSelect();
                                }}
                                className="text-[12px] font-medium text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                            >
                                {action.label}
                            </button>
                        ))}
                        {showSelectAllNone ? (
                            <button
                                type="button"
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onSelectNone();
                                }}
                                className="text-[12px] font-medium text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                            >
                                {selectNoneLabel}
                            </button>
                        ) : null}
                    </div>
                ) : null}
                {children}
                {footer ? (
                    <div
                        className="border-t border-gray-200 p-2"
                        onMouseDown={event => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                    >
                        {footer}
                    </div>
                ) : null}
            </rsComponents.MenuList>
        );
    };
}

function ReneoSelectInner<TOption extends ReneoSelectOption = ReneoSelectOption, TMulti extends boolean = false>(props: ReneoSelectProps<TOption, TMulti>) {
    const { t } = useTranslation('main');
    const {
        labelKey = 'label' as keyof TOption,
        valueKey = 'value' as keyof TOption,
        optionRenderer,
        labelRenderer,
        multi = false as TMulti,
        emitObject = false,
        maxSelections,
        selectAllNone = false,
        multiMenuActions = [],
        menuFooter,
        menuPortalTarget,
        size = 'md',
        disabled,
        readOnly,
        creatable = false,
        chipTone,
        valueAlign,
        tableCell,
        multiMaxVisibleTags,
        multiHiddenCountTitle,
        optionCheckboxes = false,
        compactMultiTags = false,
        menuMinWidth,
        options: optionsProp,
        value: valueProp,
        onChange,
        placeholder = 'Select...',
        className,
        components: userComponents,
        theme: userTheme,
        ...rest
    } = props;

    const isDisabled = Boolean(disabled);
    const getOptionLabelFn = useCallback((opt: TOption) => getOptionLabel(opt, labelKey), [labelKey]);
    const getOptionValueFn = useCallback((opt: TOption) => getOptionValue(opt, valueKey), [valueKey]);
    const styles = useMemo(
        () =>
            createReneoStyles<TOption>(size, {
                multi,
                chipTone,
                valueAlign,
                tableCell,
                readOnlyControl: readOnly && !isDisabled,
                compactMultiRow: multi ? multiMaxVisibleTags != null || compactMultiTags : false,
                compactMultiTags,
                optionCheckboxes,
                menuMinWidth,
            }),
        [size, multi, chipTone, valueAlign, tableCell, readOnly, isDisabled, multiMaxVisibleTags, compactMultiTags, optionCheckboxes, menuMinWidth],
    );

    /** Default theme uses `controlHeight: 38`; form fields use 36px — sync so inner layout does not overflow. */
    const mergedTheme = useMemo(() => {
        const controlHeight = getReneoSelectControlHeightPx(size);
        return (baseTheme: Theme) => {
            const patched: Theme = {
                ...baseTheme,
                spacing: { ...baseTheme.spacing, controlHeight },
            };
            if (userTheme == null) return patched;
            if (typeof userTheme === 'function') return userTheme(patched);
            return {
                ...patched,
                ...userTheme,
                spacing: { ...patched.spacing, ...(userTheme.spacing ?? {}) },
            };
        };
    }, [size, userTheme]);

    const resolvedValue = useMemo(() => {
        const flat = flattenOptions(optionsProp);
        return multi
            ? resolveMultiValue(valueProp as TOption[] | string[] | undefined, flat, labelKey, valueKey)
            : resolveSingleValue(valueProp as TOption | string | null | undefined, flat, labelKey, valueKey);
    }, [valueProp, optionsProp, multi, valueKey, labelKey]);

    const handleChange = useCallback(
        (selected: SingleValue<TOption> | MultiValue<TOption>) => {
            if (readOnly || !onChange) return;
            if (multi) {
                const arr = Array.isArray(selected) ? selected : [];
                if (maxSelections != null && arr.length > maxSelections) return;

                const currentMulti = Array.isArray(resolvedValue) ? resolvedValue : [];
                if (arr.length === 0 && currentMulti.length > 0) {
                    const disabledOptions = currentMulti.filter(option => 'disabled' in option && option.disabled);
                    onChange(handleMultiChange(disabledOptions, emitObject, valueKey) as ReneoValue<TOption, TMulti>);
                    return;
                }

                onChange(handleMultiChange(arr, emitObject, valueKey) as ReneoValue<TOption, TMulti>);
            } else {
                onChange(handleSingleChange(selected as TOption | null, emitObject, valueKey) as ReneoValue<TOption, TMulti>);
            }
        },
        [onChange, multi, emitObject, valueKey, maxSelections, readOnly, resolvedValue],
    );

    const handleClearAll = useCallback(() => {
        if (!onChange) return;
        if (multi) {
            onChange([] as ReneoValue<TOption, TMulti>);
        } else {
            onChange(undefined as ReneoValue<TOption, TMulti>);
        }
    }, [onChange, multi]);

    const handleSelectAll = useCallback(() => {
        if (readOnly || !onChange) return;
        const flat = flattenOptions(optionsProp);
        const limited = maxSelections != null ? flat.slice(0, maxSelections) : flat;
        const out = emitObject ? limited : limited.map(o => getOptionValue(o, valueKey));
        onChange(out as ReneoValue<TOption, TMulti>);
    }, [optionsProp, onChange, maxSelections, emitObject, valueKey, readOnly]);

    const components = useMemo(() => {
        const base = {
            Option: createOptionComponent(labelKey, optionRenderer, optionCheckboxes),
            MenuList:
                (multi && (selectAllNone || multiMenuActions.length > 0)) || menuFooter
                    ? createMenuListWithBulkActions<TOption>(
                          Boolean(multi && selectAllNone),
                          handleSelectAll,
                          handleClearAll,
                          multi ? multiMenuActions : [],
                          t('filterBar.selectAll'),
                          t('filterBar.selectNone'),
                          menuFooter,
                      )
                    : createMenuListWithWheelFix<TOption>(),
        };

        if (multi) {
            const multiComponents = {
                ...base,
                ...(multiMaxVisibleTags != null
                    ? {
                          MultiValue: createLimitedMultiValueComponent<TOption>(
                              multiMaxVisibleTags,
                              hiddenCount => multiHiddenCountTitle?.(hiddenCount) ?? t('filterBar.moreSelected', { count: hiddenCount }),
                              tableCell,
                          ),
                      }
                    : {}),
                MultiValueRemove: createMultiValueRemoveComponent<TOption>(),
                MultiValueLabel: createMultiValueLabelComponent<TOption>(
                    labelRenderer ? (labelRenderer as (option: TOption) => React.ReactNode) : (option: TOption) => getMultiValueDisplayLabel(option, labelKey),
                ),
            };
            return multiComponents;
        }

        if (labelRenderer) {
            return {
                ...base,
                SingleValue: createSingleValueComponent<TOption>(labelRenderer as (option: TOption) => React.ReactNode, valueAlign),
            };
        }

        return base;
    }, [
        labelKey,
        optionRenderer,
        optionCheckboxes,
        multi,
        selectAllNone,
        handleSelectAll,
        handleClearAll,
        labelRenderer,
        valueAlign,
        multiMenuActions,
        menuFooter,
        multiMaxVisibleTags,
        multiHiddenCountTitle,
        tableCell,
        t,
    ]);

    const formatOptionLabelFn = useCallback((opt: TOption) => (labelRenderer ? labelRenderer(opt) : getOptionLabelFn(opt)), [labelRenderer, getOptionLabelFn]);

    const isOptionDisabledFn = useCallback(
        (option: TOption) => {
            if ('disabled' in option && option.disabled) {
                return true;
            }
            return isOptionDisabled(option, resolvedValue as TOption[] | undefined, maxSelections, valueKey);
        },
        [maxSelections, resolvedValue, valueKey],
    );

    const selectProps = {
        value: resolvedValue,
        onChange: handleChange,
        options: (optionsProp ?? []) as readonly TOption[],
        getOptionLabel: getOptionLabelFn,
        getOptionValue: getOptionValueFn,
        formatOptionLabel: labelRenderer ? formatOptionLabelFn : undefined,
        placeholder,
        isClearable: !readOnly,
        isSearchable: !readOnly,
        isDisabled: isDisabled,
        tabSelectsValue: false,
        closeMenuOnSelect: !multi,
        hideSelectedOptions: false,
        menuPortalTarget: menuPortalTarget ?? DEFAULT_MENU_PORTAL,
        menuPosition: 'fixed' as const,
        styles,
        theme: mergedTheme,
        className: cn('reneo-select', className),
        isMulti: multi,
        backspaceRemovesValue: multi ? !readOnly : true,
        isOptionDisabled: isOptionDisabledFn,
        ...rest,
        components: {
            ...(userComponents ?? {}),
            ...components,
        },
    };

    if (creatable) {
        const { isMulti: _im, ...creatableProps } = selectProps;
        return (
            <CreatableSelect<TOption, true, GroupBase<TOption>>
                {...(creatableProps as React.ComponentProps<typeof CreatableSelect<TOption, true, GroupBase<TOption>>>)}
                isMulti
                formatCreateLabel={(inputValue: string) => `Create "${inputValue}"`}
            />
        );
    }

    return <Select<TOption, boolean, GroupBase<TOption>> {...selectProps} />;
}

export const ReneoSelect = ReneoSelectInner as <TOption extends ReneoSelectOption = ReneoSelectOption, TMulti extends boolean = false>(
    props: ReneoSelectProps<TOption, TMulti>,
) => React.ReactElement;

/** @deprecated Use `ReneoSelectOption` — kept for imports that used `MultiSelectReact` types. */
export type SelectOption = ReneoSelectOption;

/** Grouped options for react-select — same shape as former `SelectGroupOption` from `multi-select-react`. */
export type SelectGroupOption = GroupBase<ReneoSelectOption>;
